import { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Radio, RefreshCw, TrendingUp, CheckCircle, Settings, Info, Zap, ChevronDown, Webhook, Copy, Check, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SignalDetailDialog } from '@/components/SignalDetailDialog';
import { getAlertSettings, saveAlertSettings, getPatternProbability, savePatternAlert, findPatternAlertBySignal } from '@/utils/localStorage';

// Public-facing host shown in webhook examples and UI copy. The backend can
// continue to live at the preview/internal host — only the display string
// is rewritten so users see the product domain.
const PUBLIC_WEBHOOK_HOST = 'https://tradingpilot360.com';

export default function SignalScanner() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [alertSettings, setAlertSettings] = useState(getAlertSettings());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailSignal, setDetailSignal] = useState(null);
  const { token } = useAuth();
  const [processedAlerts, setProcessedAlerts] = useState(new Set());
  const [patternCache, setPatternCache] = useState({});
  const [signalMode, setSignalMode] = useState('system'); // 'system' | 'custom'
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  // Live "NEW" indicator — track signal IDs that just arrived so we can glow
  // them for ~8s then fade to normal. We use a Ref for "already seen" IDs so
  // we don't flash the whole list on first mount, and a Set in state so the
  // UI re-renders when badges expire.
  const seenSignalIdsRef = useRef(null); // null = not yet seeded
  const newSignalTimersRef = useRef(new Map()); // id -> timeout handle
  const [newSignalIds, setNewSignalIds] = useState(() => new Set());
  const NEW_BADGE_MS = 8000;

  const flagSignalAsNew = useCallback((id) => {
    setNewSignalIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    // Clear any existing timer for this id, then schedule expiry
    const existing = newSignalTimersRef.current.get(id);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      setNewSignalIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      newSignalTimersRef.current.delete(id);
    }, NEW_BADGE_MS);
    newSignalTimersRef.current.set(id, timer);
  }, []);

  // Cleanup pending timers on unmount
  useEffect(() => {
    const timers = newSignalTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);
  
  // Calculate pattern probability for a signal with caching
  const getSignalPattern = useCallback((signal) => {
    const cacheKey = `${signal.id}-${signal.signal}-${signal.timeframe}-${signal.symbol}`;
    if (patternCache[cacheKey]) {
      return patternCache[cacheKey];
    }
    const result = getPatternProbability(
      signal.signal,
      signal.timeframe,
      signal.symbol,
      alertSettings
    );
    setPatternCache(prev => ({ ...prev, [cacheKey]: result }));
    return result;
  }, [alertSettings, patternCache]);
  
  // Save high probability alert to history (toast disabled for mobile)
  const showPatternAlert = useCallback((signal, pattern) => {
    if (!alertSettings.patternAlertsEnabled) return;
    if (pattern.level !== 'high' && pattern.level !== 'moderate') return;
    if (processedAlerts.has(signal.id)) return;
    
    setProcessedAlerts(prev => new Set([...prev, signal.id]));
    
    // Save pattern alert to history for tracking
    try {
      savePatternAlert({
        signalId: signal.id,
        asset: signal.symbol,
        signalType: signal.signal,
        timeframe: signal.timeframe,
        historicalWinRate: pattern.analysis.winRate,
        patternLevel: pattern.level
      });
    } catch (error) {
      console.error('Error saving pattern alert:', error);
    }
    
    // Toast notifications disabled for mobile usability
  }, [alertSettings.patternAlertsEnabled, processedAlerts]);
  
  useEffect(() => {
    loadSignals();
    
    // Auto-refresh every 15 seconds if enabled (increased from 5s to reduce mobile load)
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadSignals(true); // Silent refresh
      }, 15000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  // Load the user's current signal mode (system/custom). Drives whether we
  // surface the calm "automation connected" status or the advanced webhook
  // tooling for power users.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/paper-trading/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setSignalMode(data.signal_mode || 'system');
        if (data.signal_mode === 'custom') {
          // Lazy-load the webhook URL only when the user is actually on
          // custom mode — avoids exposing it to default beginners.
          const info = await fetch(`${backendUrl}/api/paper-trading/webhook-info`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (info.ok && !cancelled) setWebhookInfo(await info.json());
        }
      } catch {
        /* silent — falls back to system mode */
      }
    })();
    return () => { cancelled = true; };
  }, [token, backendUrl]);
  
  const loadSignals = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const response = await fetch(`${backendUrl}/api/signals`);
      if (!response.ok) throw new Error('Failed to fetch signals');
      
      const data = await response.json();

      // Track "NEW" indicators — on very first load, seed the seen-set without
      // flashing existing history. Afterward, any signal whose id wasn't
      // previously seen is flagged as NEW for ~8s.
      if (seenSignalIdsRef.current === null) {
        seenSignalIdsRef.current = new Set(data.map((s) => s.id));
      } else {
        for (const s of data) {
          if (!seenSignalIdsRef.current.has(s.id)) {
            seenSignalIdsRef.current.add(s.id);
            flagSignalAsNew(s.id);
          }
        }
      }

      // Check for new signals and trigger pattern alerts
      if (data.length > signals.length) {
        const newSignals = data.slice(0, data.length - signals.length);
        newSignals.forEach(newSignal => {
          const pattern = getSignalPattern(newSignal);
          
          // Signal logging continues - toast notifications disabled for mobile usability
          console.log(`Signal detected: ${newSignal.signal.toUpperCase()} - ${newSignal.symbol} @ ${newSignal.price}`);
          
          // Save to history if applicable
          if (pattern.level === 'high' || pattern.level === 'moderate') {
            setTimeout(() => showPatternAlert(newSignal, pattern), 500);
          }
        });
      }
      
      setSignals(data);
    } catch (error) {
      console.error('Error loading signals:', error);
      if (!silent) toast.error('Failed to load signals');
    } finally {
      if (!silent) setLoading(false);
    }
  };
  
  const handleSaveSettings = () => {
    saveAlertSettings(alertSettings);
    setSettingsOpen(false);
    toast.success('Alert settings saved');
  };
  
  const handleTestSignal = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/signals/test`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Failed to generate test signal');
      
      toast.success('Test signal generated!');
      loadSignals();
    } catch (error) {
      console.error('Error generating test signal:', error);
      toast.error('Failed to generate test signal');
    }
  };
  
  const getSignalColor = (signal) => {
    switch (signal.toLowerCase()) {
      case 'pullback':
        return 'text-primary';
      case 'breakout':
        return 'text-success';
      case 'reversal':
        return 'text-warning';
      case 'continuation':
        return 'text-foreground';
      default:
        return 'text-muted-foreground';
    }
  };
  
  const getSignalBadgeColor = (signal) => {
    switch (signal.toLowerCase()) {
      case 'pullback':
        return 'bg-primary/20 text-primary border-primary/50';
      case 'breakout':
        return 'bg-success/20 text-success border-success/50';
      case 'reversal':
        return 'bg-warning/20 text-warning border-warning/50';
      case 'continuation':
        return 'bg-foreground/20 text-foreground border-foreground/50';
      default:
        return 'bg-muted/20 text-muted-foreground border-muted/50';
    }
  };
  
  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'Strong Setup':
        return {
          bg: 'bg-success/20',
          text: 'text-success',
          border: 'border-success/50',
          badge: 'bg-success text-success-foreground'
        };
      case 'Good Setup':
        return {
          bg: 'bg-primary/20',
          text: 'text-primary',
          border: 'border-primary/50',
          badge: 'bg-primary text-primary-foreground'
        };
      case 'Weak Setup':
        return {
          bg: 'bg-warning/20',
          text: 'text-warning',
          border: 'border-warning/50',
          badge: 'bg-warning text-warning-foreground'
        };
      case 'Avoid':
        return {
          bg: 'bg-destructive/20',
          text: 'text-destructive',
          border: 'border-destructive/50',
          badge: 'bg-destructive text-destructive-foreground'
        };
      default:
        return {
          bg: 'bg-muted/20',
          text: 'text-muted-foreground',
          border: 'border-muted/50',
          badge: 'bg-muted text-muted-foreground'
        };
    }
  };
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-primary';
    if (score >= 40) return 'text-warning';
    return 'text-destructive';
  };
  
  const getPatternBadge = (pattern) => {
    switch (pattern.level) {
      case 'high':
        return {
          icon: '🟢',
          label: 'High Probability',
          className: 'bg-success/20 text-success border-success/50'
        };
      case 'moderate':
        return {
          icon: '🟡',
          label: 'Moderate',
          className: 'bg-warning/20 text-warning border-warning/50'
        };
      case 'low':
        return {
          icon: '🔴',
          label: 'Low Probability',
          className: 'bg-destructive/20 text-destructive border-destructive/50'
        };
      case 'insufficient':
      default:
        return {
          icon: '⚪',
          label: 'Insufficient Data',
          className: 'bg-muted/20 text-muted-foreground border-muted/50'
        };
    }
  };
  
  const handleValidateTrade = (signal) => {
    // Find if there's a pattern alert for this signal
    const patternAlert = findPatternAlertBySignal(signal.id);
    
    // Navigate to validator with pre-filled data
    navigate('/validator', {
      state: {
        signalData: {
          signalId: signal.id,
          symbol: signal.symbol,
          price: signal.price,
          timeframe: signal.timeframe,
          signalType: signal.signal,
          patternAlertId: patternAlert?.id || null
        }
      }
    });
    
    toast.info(`Opening validator for ${signal.symbol}`);
  };
  
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="mb-6 sm:mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <Radio className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter text-foreground">
              Signal Scanner
            </h1>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground font-medium">
            Receive and monitor TradingView webhook alerts in real-time.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-3 md:flex-nowrap">
          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="alert-settings-btn"
                variant="outline"
                className="border-border hover:bg-accent"
              >
                <Settings className="w-4 h-4 mr-2" />
                Alert Settings
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-card border-border/40">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-primary" />
                  <span>Signal Pattern Alerts</span>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border/40">
                  <div className="space-y-1">
                    <Label htmlFor="patternAlertsEnabled" className="font-semibold cursor-pointer">
                      Enable Pattern Alerts
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Get notified when signals match high-performing patterns
                    </p>
                  </div>
                  <Switch
                    id="patternAlertsEnabled"
                    data-testid="pattern-alerts-toggle"
                    checked={alertSettings.patternAlertsEnabled}
                    onCheckedChange={(checked) => 
                      setAlertSettings(prev => ({ ...prev, patternAlertsEnabled: checked }))
                    }
                  />
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="minWinRate" className="font-semibold">
                    Minimum Win Rate Threshold
                  </Label>
                  <div className="flex items-center space-x-3">
                    <Input
                      id="minWinRate"
                      data-testid="min-winrate-input"
                      type="number"
                      min="0"
                      max="100"
                      value={alertSettings.minWinRateThreshold}
                      onChange={(e) => 
                        setAlertSettings(prev => ({ 
                          ...prev, 
                          minWinRateThreshold: parseInt(e.target.value) || 60 
                        }))
                      }
                      className="w-24 bg-secondary/20 border-border/50 font-mono"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Signals with win rate above this threshold are marked as "High Probability"
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="minTrades" className="font-semibold">
                    Minimum Trades Required
                  </Label>
                  <div className="flex items-center space-x-3">
                    <Input
                      id="minTrades"
                      data-testid="min-trades-input"
                      type="number"
                      min="1"
                      max="100"
                      value={alertSettings.minTradesRequired}
                      onChange={(e) => {
                        const val = e.target.value;
                        setAlertSettings(prev => ({ 
                          ...prev, 
                          minTradesRequired: val === '' ? '' : parseInt(val) || 1
                        }));
                      }}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value);
                        if (!val || val < 1) {
                          setAlertSettings(prev => ({ ...prev, minTradesRequired: 1 }));
                        }
                      }}
                      className="w-24 bg-secondary/20 border-border/50 font-mono"
                    />
                    <span className="text-sm text-muted-foreground">trades</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Patterns with fewer trades show "Insufficient Data"
                  </p>
                </div>
                
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/30">
                  <div className="flex items-start space-x-2">
                    <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-muted-foreground">
                      <p className="font-semibold text-foreground mb-1">Pattern Match Levels</p>
                      <p>🟢 <span className="text-success">High Probability</span>: Win rate ≥ {alertSettings.minWinRateThreshold}%</p>
                      <p>🟡 <span className="text-warning">Moderate</span>: Win rate {alertSettings.minWinRateThreshold - 15}% - {alertSettings.minWinRateThreshold - 1}%</p>
                      <p>🔴 <span className="text-destructive">Low Probability</span>: Win rate &lt; {alertSettings.minWinRateThreshold - 15}%</p>
                      <p>⚪ <span className="text-muted-foreground">Insufficient Data</span>: &lt; {alertSettings.minTradesRequired} trades</p>
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={handleSaveSettings}
                  data-testid="save-alert-settings-btn"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  Save Settings
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="auto-refresh-btn"
            variant={autoRefresh ? "default" : "outline"}
            className={autoRefresh ? "bg-success hover:bg-success/90" : ""}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          
          <Button
            onClick={handleTestSignal}
            data-testid="test-signal-btn"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Test Signal
          </Button>
        </div>
      </div>
      
      <Card className="p-4 sm:p-6 border-border/40 bg-card mb-4 sm:mb-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${autoRefresh ? 'bg-success animate-pulse' : 'bg-muted'}`} />
              <span className="text-sm font-semibold text-foreground">
                {autoRefresh ? 'Live Monitoring' : 'Paused'}
              </span>
            </div>

            <div className="hidden sm:block h-4 w-px bg-border" />

            <div className="flex items-center space-x-2">
              <span className="text-xs sm:text-sm text-muted-foreground">Total Signals:</span>
              <span className="text-base sm:text-lg font-bold font-mono text-foreground" data-testid="total-signals">
                {signals.length}
              </span>
              {signals.length > 50 && (
                <span className="text-[10px] sm:text-xs text-muted-foreground">(showing latest 50)</span>
              )}
            </div>
          </div>

          <Button
            onClick={() => loadSignals()}
            data-testid="refresh-btn"
            variant="ghost"
            size="sm"
            disabled={loading}
            className="shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </Card>
      
      <Card className="border-border/40 bg-card">
        <div>
          {signals.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Radio className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="font-medium">No signals received yet</p>
              <p className="text-sm mt-2">Click "Test Signal" to simulate a webhook or wait for TradingView alerts</p>
            </div>
          ) : (
            <>
              {/* Mobile compact list (<768px) */}
              <ul
                className="md:hidden divide-y divide-border/20"
                data-testid="signal-list-mobile"
              >
                {signals.slice(0, 50).map((signal) => {
                  const recColor = getRecommendationColor(signal.recommendation);
                  const pattern = getSignalPattern(signal);
                  const patternBadge = getPatternBadge(pattern);
                  const isNew = newSignalIds.has(signal.id);
                  return (
                    <li
                      key={signal.id}
                      className={`px-3 sm:px-4 py-2.5 active:bg-accent/40 transition-colors ${
                        isNew ? 'signal-row-new-glow' : ''
                      }`}
                      data-testid={`signal-row-mobile-${signal.id}`}
                      data-is-new={isNew ? 'true' : 'false'}
                    >
                      <button
                        type="button"
                        onClick={() => setDetailSignal(signal)}
                        className="w-full flex items-center gap-2 text-left"
                        data-testid={`signal-row-toggle-${signal.id}`}
                      >
                        {isNew && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border border-success/40 bg-success/15 text-success flex-shrink-0"
                            data-testid={`signal-new-badge-${signal.id}`}
                            aria-label="new signal"
                          >
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-success"></span>
                            </span>
                            NEW
                          </span>
                        )}
                        <span className="font-semibold text-muted-foreground text-xs flex-shrink-0 w-14 truncate">
                          {signal.symbol}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border flex-shrink-0 ${getSignalBadgeColor(signal.signal)}`}>
                          {signal.signal.toUpperCase().slice(0, 4)}
                        </span>
                        <span
                          className={`text-xl font-black font-mono tabular-nums leading-none flex-shrink-0 ${getScoreColor(signal.score)}`}
                          data-testid={`signal-score-mobile-${signal.id}`}
                        >
                          {signal.score}
                        </span>
                        <span className="ml-auto font-mono text-[10px] text-muted-foreground/70 flex-shrink-0">
                          {format(new Date(signal.timestamp), 'HH:mm')}
                        </span>
                        <ChevronDown
                          className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 -rotate-90"
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Desktop full table (≥768px) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
              <thead className="border-b border-border/40">
                <tr className="text-left">
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground whitespace-nowrap">Time</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground">Symbol</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground">Price</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground">TF</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground">Signal</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground">Pattern</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground">Score</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground">Status</th>
                  <th className="px-2 py-2 font-bold text-[10px] text-muted-foreground"></th>
                </tr>
              </thead>
              <tbody>
                {signals.slice(0, 50).map((signal) => {
                  const recColor = getRecommendationColor(signal.recommendation);
                  const pattern = getSignalPattern(signal);
                  const patternBadge = getPatternBadge(pattern);
                  const isNew = newSignalIds.has(signal.id);
                  return (
                    <tr
                      key={signal.id}
                      data-testid={`signal-row-${signal.id}`}
                      data-is-new={isNew ? 'true' : 'false'}
                      onClick={() => setDetailSignal(signal)}
                      className={`border-b border-border/20 hover:bg-accent/50 transition-colors cursor-pointer ${
                        isNew ? 'signal-row-new-glow' : ''
                      }`}
                    >
                      <td className="px-2 py-2 font-mono text-[10px] whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {isNew && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold border border-success/40 bg-success/15 text-success flex-shrink-0"
                              data-testid={`signal-new-badge-${signal.id}`}
                              aria-label="new signal"
                            >
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-success"></span>
                              </span>
                              NEW
                            </span>
                          )}
                          {format(new Date(signal.timestamp), 'MMM dd, HH:mm')}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="font-bold text-foreground text-[10px]">{signal.symbol}</span>
                      </td>
                      <td className="px-2 py-2">
                        <span className="font-mono text-[10px] text-foreground">{signal.price}</span>
                      </td>
                      <td className="px-2 py-2">
                        <span className="text-[10px] text-foreground">{signal.timeframe}</span>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${getSignalBadgeColor(signal.signal)}`}>
                          {signal.signal.toUpperCase().slice(0, 4)}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <div 
                          className={`px-1.5 py-0.5 rounded text-[9px] font-bold border inline-flex items-center ${patternBadge.className}`}
                          data-testid={`pattern-badge-${signal.id}`}
                          title={pattern.level !== 'insufficient' 
                            ? `Win Rate: ${pattern.analysis.winRate}% (${pattern.analysis.totalTrades} trades)` 
                            : 'Not enough historical data'
                          }
                        >
                          <span>{patternBadge.icon}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={`text-lg font-black font-mono tabular-nums leading-none ${getScoreColor(signal.score)}`}
                          data-testid={`signal-score-${signal.id}`}
                        >
                          {signal.score}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap ${recColor.badge}`} data-testid={`signal-recommendation-${signal.id}`}>
                          {signal.recommendation.split(' ')[0]}
                        </span>
                      </td>
                      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          onClick={() => handleValidateTrade(signal)}
                          data-testid={`validate-signal-${signal.id}`}
                          size="sm"
                          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-[10px] h-6 px-2"
                        >
                          <CheckCircle className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
              </div>
            </>
          )}
        </div>
      </Card>
      
      {signalMode === 'system' ? (
        // Beginner default: calm, automated, no infra wiring required.
        <Card
          className="p-5 mt-6 border-success/20 bg-success/5"
          data-testid="system-status-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-success/15 border border-success/30 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground">
                System automation connected
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Trading Pilot 360 is already monitoring and executing qualified setups
                automatically. No setup required — just leave it running.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        // Advanced (custom) mode — power users only.
        <Card
          className="p-5 mt-6 border-primary/20 bg-card/50"
          data-testid="custom-webhook-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <Webhook className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-bold text-foreground">Your private TradingView webhook</h3>
          </div>
          {webhookInfo ? (
            <>
              <div className="flex items-stretch gap-2">
                <code
                  className="flex-1 min-w-0 px-2.5 py-2 rounded bg-background border border-border/40 text-[11px] font-mono truncate"
                  data-testid="webhook-url"
                  title={`${PUBLIC_WEBHOOK_HOST}${webhookInfo.webhook_path}`}
                >
                  {`${PUBLIC_WEBHOOK_HOST}${webhookInfo.webhook_path}`}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-auto px-2"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${PUBLIC_WEBHOOK_HOST}${webhookInfo.webhook_path}`);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    } catch {
                      toast.error('Copy failed — select and copy manually.');
                    }
                  }}
                  data-testid="webhook-copy-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                Paste this into TradingView → Alerts → Webhook URL. Send JSON:{' '}
                <code className="font-mono text-foreground">{`{ symbol, price, timeframe, signal }`}</code>
              </p>
              <div
                className="mt-3 flex items-start gap-2 p-2 rounded bg-success/5 border border-success/20"
                data-testid="webhook-safety-note"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-success mt-0.5 shrink-0" />
                <p className="text-[10px] leading-relaxed text-muted-foreground">
                  Risk engine stays on — SL/TP, risk %, daily loss cap, and loss-streak
                  pause cannot be disabled.
                </p>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Loading your webhook URL…</p>
          )}
        </Card>
      )}

      {/* Signal Detail — transparency view (no manual execution). */}
      <SignalDetailDialog
        signal={detailSignal}
        open={!!detailSignal}
        onOpenChange={(v) => !v && setDetailSignal(null)}
        token={token}
      />
    </div>
  );
}
